import type { ComponentProps, FC, ReactNode } from 'react';
import { Button, type COLOR } from '@george-labs.com/design-system';

import { t } from '@luna/i18n';
import { useActiveCustomizationFeature } from '@shared/products-core';
import { ProductColorChangeDropdown } from '@retail/products-customisation';
import { type ColorId, CustomizationService } from '@api-client/products';

import type { ActivePAPIProductWithAvatarData } from '../types';
import { useCustomizationSuccessHandler } from './hooks/useCustomizationSuccessHandler';
import { getColorStrengthHexValues } from '../services/colorService';
import { getCustomizationWithFallback } from '../services/fallbackCustomizationValueService';
import { useActivePAPIProduct } from '../../../ActivePAPIProductContext';

interface Props {
    toggle?: ReactNode;
    trigger?: ComponentProps<typeof ProductColorChangeDropdown>['trigger'];
}

export const ColorChange: FC<Props> = ({ toggle, trigger = 'productDetail' }) => {
    const { activateColorChangeCustomization } = useActiveCustomizationFeature();
    const {
        id,
        type,
        _embedded: { customization },
    } = useActivePAPIProduct<ActivePAPIProductWithAvatarData>();
    const handleSuccessfulCustomization = useCustomizationSuccessHandler();

    const handleColorSave = async (color: COLOR) => {
        const colorId = color.toUpperCase() as ColorId;

        return CustomizationService.putPublicProductsCustomizationColor({
            id,
            requestBody: {
                color: colorId,
            },
        }).then((customizationResponse) => {
            const { weak: hexCodeLight, strong: hexCodeDark } = getColorStrengthHexValues(color);

            handleSuccessfulCustomization(
                getCustomizationWithFallback(
                    {
                        ...customization,
                        color: {
                            id: colorId,
                            hexCodeLight,
                            hexCodeDark,
                        },
                    },
                    customizationResponse,
                ),
            );
        });
    };

    const colorChangeToggle = toggle || (
        <Button
            icon={Button.ICON.PALETTE}
            size={Button.SIZE.SMALL}
            onClick={activateColorChangeCustomization}
            data-cy="product-edit-color-button"
            className="g-inverted"
        >
            {t('mainOverview.action.changeColor_a11y')}
        </Button>
    );

    return (
        <ProductColorChangeDropdown
            data-cy="products-color-change-dropdown"
            toggle={colorChangeToggle}
            actionButtonDataCy="product-edit-color-save-button"
            onColorSave={handleColorSave}
            colorChangeContext="details"
            trigger={trigger}
            accountType={type}
        />
    );
};
